import type { OrgTipo } from './navigation';
import { orgBrand, orgLabel } from './org-branding';

export type TourStep = {
  id: string;
  title: string;
  body: string;
  /** Selector `[data-tour="…"]` — omitir = modal centrado */
  target?: string;
  /** Cambiar pestaña antes de mostrar el paso */
  tab?: string;
  icon?: string;
};

const PENDING_KEY = 'toscana.onboarding.pending';
const DONE_PREFIX = 'toscana.onboarding.done.';

export type OnboardingPending = {
  tenantId: string;
  nombre: string;
  orgTipo?: string | null;
};

export function markOnboardingPending(p: OnboardingPending): void {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(p));
  } catch { /* ignore */ }
}

export function consumeOnboardingPending(tenantId: string): OnboardingPending | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as OnboardingPending;
    if (p.tenantId !== tenantId) return null;
    sessionStorage.removeItem(PENDING_KEY);
    return p;
  } catch {
    return null;
  }
}

export function isOnboardingDone(tenantId: string): boolean {
  try {
    return localStorage.getItem(DONE_PREFIX + tenantId) === '1';
  } catch {
    return false;
  }
}

export function markOnboardingDone(tenantId: string): void {
  try {
    localStorage.setItem(DONE_PREFIX + tenantId, '1');
    sessionStorage.removeItem(PENDING_KEY);
  } catch { /* ignore */ }
}

export function clearOnboardingDone(tenantId: string): void {
  try {
    localStorage.removeItem(DONE_PREFIX + tenantId);
  } catch { /* ignore */ }
}

/** Pasos del tour según rubro y módulos visibles. */
export function buildTourSteps(
  tenantName: string,
  orgTipo: string | null | undefined,
  visibleTabs: Set<string>,
): TourStep[] {
  const org = orgBrand(orgTipo);
  const rubro = orgLabel(orgTipo);
  const steps: TourStep[] = [
    {
      id: 'welcome',
      icon: '👋',
      title: `¡Bienvenido, ${tenantName}!`,
      body: `Esta es su clínica de ${rubro}. En los próximos pasos le mostramos dónde está cada cosa para que empiece sin confusión. Tarda menos de 1 minuto.`,
    },
    {
      id: 'brand',
      target: 'sidebar-brand',
      icon: org.emoji,
      title: 'Su identidad',
      body: 'Aquí ve el nombre de su clínica. Más adelante puede subir su logo y elegir colores en Configuración — no tiene que quedarse con los colores de Toscana.',
    },
    {
      id: 'menu',
      target: 'sidebar-nav',
      icon: '📂',
      title: 'Menú principal',
      body: 'Todo el sistema se organiza por secciones: operación diaria, clínica, finanzas y administración. Solo verá los módulos incluidos en su plan.',
    },
    {
      id: 'dashboard',
      target: 'nav-dashboard',
      tab: 'dashboard',
      icon: '📊',
      title: 'Panel de inicio',
      body: 'Resumen del día: citas, pacientes y accesos rápidos. Siempre vuelva aquí para orientarse.',
    },
  ];

  if (visibleTabs.has('agenda')) {
    steps.push({
      id: 'agenda',
      target: 'nav-agenda',
      tab: 'agenda',
      icon: '📅',
      title: 'Agenda de citas',
      body: 'Programe, confirme y reprograme citas. Puede compartir un enlace público para que pacientes agenden en línea.',
    });
  }

  const pacientesTab = visibleTabs.has('pacientes') ? 'pacientes'
    : visibleTabs.has('expedienteVet') ? 'expedienteVet'
      : visibleTabs.has('expediente') ? 'expediente' : null;

  if (pacientesTab) {
    const isVet = pacientesTab === 'expedienteVet';
    steps.push({
      id: 'pacientes',
      target: `nav-${pacientesTab}`,
      tab: pacientesTab,
      icon: isVet ? '🐾' : '👤',
      title: isVet ? 'Mascotas y expediente' : 'Pacientes',
      body: isVet
        ? 'Registre mascotas, dueños y el historial veterinario de cada paciente.'
        : 'Registre pacientes nuevos, busque por nombre o documento y abra su expediente clínico.',
    });
  }

  if (visibleTabs.has('cuentas') || visibleTabs.has('farmaciaPos')) {
    const t = visibleTabs.has('cuentas') ? 'cuentas' : 'farmaciaPos';
    steps.push({
      id: 'cobros',
      target: `nav-${t}`,
      tab: t,
      icon: '💳',
      title: 'Cobros y facturación',
      body: 'Genere cuentas, registre pagos y emita comprobantes. La farmacia POS también cobra desde aquí si su plan lo incluye.',
    });
  }

  if (orgTipo === 'clinica-optica' && visibleTabs.has('optica')) {
    steps.push({
      id: 'optica',
      target: 'nav-optica',
      tab: 'optica',
      icon: '👓',
      title: 'Módulo óptica',
      body: 'Graduaciones, órdenes de lentes e inventario óptico — pensado para su rubro.',
    });
  }

  if (visibleTabs.has('configuracion')) {
    steps.push({
      id: 'config',
      target: 'nav-configuracion',
      tab: 'configuracion',
      icon: '⚙️',
      title: 'Configuración',
      body: 'Cambie logo, colores de la clínica, datos de contacto y preferencias. Personalícelo con la marca de su consultorio.',
    });
  }

  steps.push({
    id: 'help',
    target: 'topbar-help',
    icon: '💡',
    title: '¿Necesita ayuda otra vez?',
    body: 'Arriba a la derecha tiene el botón "Recorrido" para repetir esta guía cuando quiera. Soporte vía WhatsApp: 3786-3061.',
  });

  steps.push({
    id: 'done',
    icon: '🚀',
    title: '¡Todo listo!',
    body: `Empiece por la Agenda, registre su primer ${orgTipo === 'clinica-veterinaria' ? 'mascota' : 'paciente'}, o explore las secciones del menú. Su clínica ya está operativa.`,
  });

  return steps;
}

export function shouldAutoStartTour(tenantId: string): boolean {
  if (isOnboardingDone(tenantId)) return false;
  try {
    if (sessionStorage.getItem(PENDING_KEY)) return true;
  } catch { /* ignore */ }
  return false;
}
